import { useEffect, useRef, type RefObject } from "react";

type RevealDirection = "up" | "left" | "right" | "scale";

interface ScrollRevealOptions {
  threshold?: number;
  delay?: number;
  direction?: RevealDirection;
}

export function useScrollReveal<T extends HTMLElement>(
  options: ScrollRevealOptions = {},
): RefObject<T | null> {
  const { threshold = 0.15, delay = 0, direction = "up" } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Set initial hidden state
    element.style.opacity = "0";
    element.style.transition = `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms`;

    switch (direction) {
      case "up":
        element.style.transform = "translateY(50px)";
        break;
      case "left":
        element.style.transform = "translateX(-50px)";
        break;
      case "right":
        element.style.transform = "translateX(50px)";
        break;
      case "scale":
        element.style.transform = "scale(0.92)";
        break;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            element.style.opacity = "1";
            element.style.transform = "translateY(0) translateX(0) scale(1)";
            observer.unobserve(element);
          }
        });
      },
      { threshold },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, delay, direction]);

  return ref;
}

/**
 * Hook to apply scroll reveal to multiple children with staggered delays.
 * Returns a ref to attach to the parent container.
 */
export function useStaggeredReveal<T extends HTMLElement>(
  staggerDelay = 150,
  direction: RevealDirection = "up",
): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const children = Array.from(container.children) as HTMLElement[];

    children.forEach((child, index) => {
      child.style.opacity = "0";
      child.style.transition = `opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${index * staggerDelay}ms, transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${index * staggerDelay}ms`;

      switch (direction) {
        case "up":
          child.style.transform = "translateY(40px)";
          break;
        case "left":
          child.style.transform = "translateX(-40px)";
          break;
        case "right":
          child.style.transform = "translateX(40px)";
          break;
        case "scale":
          child.style.transform = "scale(0.92)";
          break;
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            children.forEach((child) => {
              child.style.opacity = "1";
              child.style.transform = "translateY(0) translateX(0) scale(1)";
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [staggerDelay, direction]);

  return ref;
}
